import styled, { PrimaryHighlight } from '../../theme/styled'
import { styledComponentWithProps } from '../Styled/Shared'

const HeroBg = require('../Layout/Features/grph_element_5.svg')

export const TextButton = styled.button`
  border: 0;
  background: 0;
  border-radius: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${({ theme }) => theme.feedback.color};
  text-decoration: underline;
  font-size: ${({ theme }) => theme.feedback.fontSize};
  padding: 0;
  cursor: pointer;
`

type FeedbackButtonProps = {
  primary?: boolean
  clear?: boolean
}

export const FeedbackFormButton = styledComponentWithProps<FeedbackButtonProps>(
  styled.button
)`
  height: 45px;
  line-height: 45px;
  width: 190px;
  cursor: pointer;

  text-transform: uppercase;
  font-size: ${({ theme }) => theme.feedback.largeSize};
  font-weight: bold;


  border-radius: 0;
  border: ${({ clear, theme }) =>
    clear ? '1px solid ' + theme.colors.invert : '1px solid transparent'};

  color: ${({ theme }) => theme.colors.invert};
  background: ${({ primary, clear, theme }) => {
    if (primary) {
      return theme.colors.brand
    } else if (clear) {
      return 'transparent'
    } else {
      return theme.colors.light
    }
  }};


  &:disabled { 
    opacity: 0.4;
    cursor: default;
  }
`

export const FeedbackContent = styled.div`
  min-height: 330px;
  width: 100%;
  flex: 1 0 auto;
  padding: 25px 60px;
  text-align: center;
  display: flex;
  flex-direction: column;

  h4,
  h5 {
    color: ${({ theme }) => theme.colors.light};
    font-size: ${({ theme }) => theme.feedback.h5Size};
  }

  h5 {
    display: flex;
    flex-direction: row;

    align-items: center;

    .spacer {
      margin-right: auto;
      min-width: 40px;
    }

    .response {
      opacity: 0;
      transition: 0.2s all;
      margin-left: auto;
      color: ${({ theme }) => theme.colors.invert};
      font-size: ${({ theme }) => theme.feedback.fontSize};
      min-width: 40px;
    }

    &.ok .response {
      opacity: 1;
    }
  }

  ${TextButton} {
    margin-left: auto;
  }

  textarea {
    width: 100%;
    display: block;
    margin: 30px auto 10px;
    border: 1px solid ${({ theme }) => theme.colors.inactive};
    height: 130px;
    &.invalid {
      border: 1px solid ${({ theme }) => theme.colors.error};
    }
  }

  .split {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex: 1;
    margin: 40px 0;

    .left {
      flex: 0 0 45%;
      h2 {
        font-size: ${({ theme }) => theme.feedback.h2Size};
        font-weight: bold;
        text-align: left;
      }
    }
    .right {
      flex: 1 1 auto;
      text-align: left;
      font-size: 26px;
      margin-left: 30px;
    }
  }
`

export const FeedbackFormWrapper = styled.div`
  z-index: 1200;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;

  .feedback-modal {
    max-height: 100vh;
    overflow-y: scroll;
    width: 840px;
    background: ${({ theme }) => theme.feedback.background};
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: center;

    @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
      margin-bottom: -1px;
    }
  }

  .feedback-modal.first-page {
    background: ${({ theme }) => theme.feedback.firstPage.background};
    background: url(${HeroBg}) left no-repeat
      ${({ theme }) => theme.feedback.firstPage.background};

    color: ${({ theme }) => theme.feedback.firstPage.color};
    font-size: ${({ theme }) => theme.feedback.largeSize};

    ${FeedbackContent} {
      /* Make IE happy */
      height: 300px;
    }
  }

  .title-line {
    color: ${({ theme }) => theme.feedback.title.color};
    background: ${({ theme }) => theme.feedback.title.background};
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    flex-shrink: 0;

    h3 {
      padding: 20px;
      font-size: ${({ theme }) => theme.feedback.h3Size};
    }

    ${TextButton} {
      margin-left: auto;
      color: ${({ theme }) => theme.feedback.background};
      text-decoration: none;
      padding-right: 10px;
    }
  }

  .feedback-buttons {
    padding: 50px;

    button {
      margin-right: 10px;
    }

    button:last-child {
      margin-right: 0;
    }
  }
`

const okFaces = [
  require('./images/face0ok.svg'),
  require('./images/face1ok.svg'),
  require('./images/face2ok.svg'),
  require('./images/face3ok.svg'),
  require('./images/face4ok.svg')
]

const faces = [
  require('./images/face0.svg'),
  require('./images/face1.svg'),
  require('./images/face2.svg'),
  require('./images/face3.svg'),
  require('./images/face4.svg')
]

export const RatingButton = styledComponentWithProps<{
  active?: boolean
  idx: number
  face?: boolean
}>(styled.button)`
  background: ${({ active, idx, face }) => {
    if (!face) {
      return active ? PrimaryHighlight : '#C8CED1'
    } else {
      return `url(${active ? okFaces[idx] : faces[idx]})`
    }
  }};
  background-size: 110%;
  background-repeat: no-repeat;
  color: ${({ theme }) => theme.colors.invert};
  border: 0;
  margin: 6px 6px 15px 6px;
  cursor: pointer;

  font-size: 20px;
  border-radius: ${({ face }) => (face ? '0' : '50%')};

  width: 60px;
  height: 60px;

  text-indent: ${({ face }) => (face ? '-9999px' : 'inherit')};
  white-space: ${({ face }) => (face ? 'nowrap' : 'inherit')};
`

export const FeedbackAccordionWrapper = styled.div`
  .accordion__item {
    margin-bottom: 20px;
  }

  .accordion__item.body-hidden {
    margin-bottom: 0;
  }

  .rating-box {
    display: inline-block;

    .yes {
      float: right;
      color: ${({ theme }) => theme.colors.invert};
    }
    .no {
      float: left;
      color: ${({ theme }) => theme.colors.inactive};
    }
  }

  .accordion__title {
    margin-bottom: 20px;
    padding: 10px;
    cursor: pointer;
    margin-top: 20px;

    &.body-hidden {
      margin-top: 0px;
      background: #adb8bc;

      h4,
      h5 {
        color: ${({ theme }) => theme.colors.invert};
      }

      &.ok {
        background: ${({ theme }) => theme.colors.brand};
      }
    }
  }
`



// WEBPACK FOOTER //
// ./src/Components/FeedbackForm/FeedbackStyles.ts